import { useCompanyConfiguration } from 'init/use-company-configuration';
import { i18nKeys, useTranslation } from 'locales';
import { useLoadMail } from 'modules/Mails/hooks';
import { getLastMail } from 'modules/Mails/utils/thread';
import posthog from 'posthog-js';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router';
import { isNullish } from 'remeda';
import { type AnalyticsTrigger, captureEvent } from 'shared/analytics/capture-event';
import { useLoadExternalMailboxes } from 'shared/hooks/use-load-external-mailboxes';

export const useMailDetailReply = () => {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const company = useCompanyConfiguration();

  const { thread } = useLoadMail(id);
  const { externalMailboxes } = useLoadExternalMailboxes();

  const [isReplying, setIsReplying] = useState(false);
  const [focusAiPromptSignal, setFocusAiPromptSignal] = useState(0);

  const draftMail = thread.mails.find(mail => mail.isDraft) ?? null;

  useEffect(() => {
    if (draftMail !== null) setIsReplying(true);
  }, [draftMail]);

  const lastMail = getLastMail(thread);
  const customDomain = company.custom_domain ?? null;
  const isSameDomain =
    isNullish(customDomain) || isNullish(lastMail)
      ? false
      : lastMail.toMail.some(addr => addr.includes(customDomain)) ||
        lastMail.cc?.some(addr => addr.includes(customDomain)) === true ||
        lastMail.fromMail.includes(customDomain);

  const isReplyEnabled = Boolean(customDomain) && isSameDomain;
  const replyDisabledReason = isReplyEnabled
    ? ''
    : t(customDomain ? i18nKeys.CUSTOM_DOMAIN.NO_MATCH : i18nKeys.CUSTOM_DOMAIN.NO_DOMAIN);
  const userSignature = externalMailboxes?.data[0]?.attributes.signature ?? '';

  const openReply = (options?: { focusAiPrompt?: boolean; trigger?: AnalyticsTrigger }) => {
    captureEvent(posthog, 'mail:composer_opened', {
      source: 'reply',
      trigger: options?.trigger ?? 'button',
    });
    setIsReplying(true);
    if (options?.focusAiPrompt) setFocusAiPromptSignal(signal => signal + 1);
  };
  const closeReply = () => setIsReplying(false);

  return {
    isReplyEnabled,
    replyDisabledReason,
    isReplying,
    openReply,
    closeReply,
    focusAiPromptSignal,
    userSignature,
    draftMail,
  };
};
